import React, { useCallback, useState } from 'react'

import dayjs from 'dayjs'
import { collection, doc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'
import { BsCopy } from 'react-icons/bs'
import { PiTrashLight } from 'react-icons/pi'
import { useNavigate } from 'react-router-dom'

import { Button, DeleteLogModal, SectionSubtitle } from '../../../..'
import { useProfessors, useStudents, useUser } from '../../../../../context'
import { firestore } from '../../../../../firebase/firebaseConfig'
import { useWindowSize } from '../../../../../hooks/useWindowSize'
import { NewLog, Student } from '../../../../../types'
import { filterProfessor } from '../../../../../utilFunctions'
import { Divider, Spacer } from '../../../../global'
import {
  LightBlueMark,
  LogsRow,
  MarksContainer,
  TableContainer,
} from '../../../../lists/styles'

const LogsTable = () => {
  const navigate = useNavigate()
  const { user, setOnEditionMode } = useUser()
  const {
    studentsList,
    setActiveStudent,
    deleteLog,
    uploadLog,
    setOnLogCreation,
  } = useStudents()
  const { setActiveLog } = useProfessors()
  const date = dayjs()
  const { isMobile } = useWindowSize()

  const [copyingLogId, setCopyingLogId] = useState<string | null>(null)
  const [deletingLogId, setDeletingLogId] = useState<string | null>(null)

  const [deleteMessage, setDeleteMessage] = useState<{
    error: boolean | null
    message: string
    hasButton?: boolean
  } | null>(null)
  const [deleteData, setDeleteData] = useState<{
    studentUID: string
    logUID: string
  } | null>(null)
  const [openModal, setOpenModal] = useState<boolean>(false)

  const handleCheckLog = async (studentUID: string, logUID: string) => {
    try {
      const stuRef = doc(firestore, 'students', studentUID)
      const docRef = doc(firestore, 'students', studentUID, 'logs', logUID)

      const studentDoc = await getDoc(stuRef)
      const _doc = await getDoc(docRef)

      if (_doc.exists() && studentDoc.exists()) {
        const studentData = studentDoc.data() as Student
        setActiveStudent(studentData)
        setActiveLog(_doc.data() as NewLog)
        const studentSlug = `${studentData.name.toLowerCase()}-${studentData.lastname.toLowerCase()}`
        navigate(`/bitacoras/${studentSlug}`)
        return
      }
      const fallbackStudent =
        studentsList?.find((student) => student.uid === studentUID) ??
        studentsList?.[0]
      if (fallbackStudent) {
        const fallbackSlug = `${fallbackStudent.name.toLowerCase()}-${fallbackStudent.lastname.toLowerCase()}`
        navigate(`/bitacoras/${fallbackSlug}`)
      }
    } catch (error) {
      console.log('Error trayendo el doc', error)
    }
  }

  const handleCopyLog = useCallback(
    async (studentUID: string, logUID: string) => {
      try {
        setCopyingLogId(logUID)
        const stuRef = doc(firestore, 'students', studentUID)
        const logRef = doc(firestore, 'students', studentUID, 'logs', logUID)
        const now = dayjs().format('YYYY-MM-DD')

        const studentDoc = await getDoc(stuRef)
        const logDoc = await getDoc(logRef)

        if (logDoc.exists() && studentDoc.exists()) {
          const studentData = studentDoc.data() as Student
          setActiveStudent(studentData)
          const logsCollectionRef = collection(stuRef, 'logs')
          const logDocRef = doc(logsCollectionRef)
          const {
            uid,
            date = now,
            audio,
            ...logWithoutUID
          } = logDoc.data() as NewLog

          const updatedLogItem = { date, ...logWithoutUID, uid: logDocRef.id }

          await uploadLog(
            studentUID,
            updatedLogItem,
            studentDoc.data().activeBonus,
          )

          setActiveLog(updatedLogItem)
          setOnLogCreation(true)
          setOnEditionMode(true)
        }
        if (studentDoc.exists()) {
          const studentData = studentDoc.data() as Student
          const studentSlug = `${studentData.name.toLowerCase()}-${studentData.lastname.toLowerCase()}`
          navigate(`/bitacoras/${studentSlug}`)
        } else {
          const fallbackStudent =
            studentsList?.find((student) => student.uid === studentUID) ??
            studentsList?.[0]
          if (fallbackStudent) {
            const fallbackSlug = `${fallbackStudent.name.toLowerCase()}-${fallbackStudent.lastname.toLowerCase()}`
            navigate(`/bitacoras/${fallbackSlug}`)
          }
        }
      } catch (error) {
        console.log('Error trayendo el doc', error)
        toast.error('Error al copiar la bitácora')
      } finally {
        setCopyingLogId(null)
      }
    },
    [
      uploadLog,
      setActiveLog,
      setActiveStudent,
      setOnLogCreation,
      setOnEditionMode,
      navigate,
    ],
  )

  const handleDelete = (studentUID: string, logUID: string) => {
    setDeleteData({ studentUID, logUID })
    setOpenModal(true)
    setDeleteMessage({
      error: null,
      message: 'Seguro que quieres borrar esta bitácora?',
      hasButton: true,
    })
  }

  const handleDeleteLog = useCallback(async () => {
    if (!user || !deleteData) return

    try {
      await toast.promise(
        deleteLog(deleteData.studentUID, deleteData.logUID, user.uid),
        {
          loading: 'Eliminando bitácora...',
          success: (res) =>
            res?.message
              ? `${res.message}`
              : 'Bitácora eliminada correctamente',
          error: (err) =>
            typeof err?.message === 'string'
              ? `${err.message}`
              : 'Error al eliminar la bitácora',
        },
      )
    } catch (error) {
      console.log(error)
    } finally {
      setDeleteData(null)
      setOpenModal(false)
      setDeletingLogId(null)
    }
  }, [user, deleteData, deleteLog])

  const handleClose = () => {
    setDeleteMessage(null)
    setOpenModal(false)
    setDeleteData(null)
  }

  return (
    <>
      {deleteMessage && openModal && (
        <DeleteLogModal
          handleClose={handleClose}
          message={deleteMessage.message}
          handleDelete={handleDeleteLog}
          hasButton={deleteMessage.hasButton}
        />
      )}
      {user?.logs && (
        <TableContainer>
          <MarksContainer>
            <SectionSubtitle fontSize={12} fontWeight={500}>
              Clases individuales:
            </SectionSubtitle>
            <LightBlueMark>{date.format('MMMM')}</LightBlueMark>
            <LightBlueMark>{user?.logs.length}</LightBlueMark>
          </MarksContainer>
          <Spacer height={15} />
          <Divider />
          <Spacer height={15} />
          <div>
            {user?.logs
              .filter(
                (log) =>
                  dayjs(log.date).isSame(date, 'month') &&
                  dayjs(log.date).isSame(date, 'year'),
              )
              .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
              .map((log, idx) => {
                const studentName = filterProfessor(
                  log.studentUID,
                  studentsList,
                )
                const profileImage =
                  filterProfessor(log.studentUID, studentsList, true) ||
                  '/assets/profile.png'
                return (
                  <LogsRow key={log.logUID || idx}>
                    <div className="l-log-number">Bitacora N {idx + 1}</div>
                    <div className="l-red">
                      Cargada el {dayjs(log.date).format('DD/MM/YYYY')}{' '}
                    </div>
                    <div className="l-student with-image">
                      <div
                        className="profile-image"
                        style={{
                          backgroundImage: `url(${profileImage})`,
                        }}
                      ></div>
                      <span className="l-yellow">Alumno:</span>
                      <span
                        style={{
                          marginLeft: '5px',
                          textTransform: 'capitalize',
                        }}
                      >
                        {studentName}
                      </span>
                    </div>
                    <div className="l-actions">
                      <Button
                        label={`Ver ${!isMobile ? 'bitácora' : ''}`}
                        onClick={() =>
                          handleCheckLog(log.studentUID, log.logUID)
                        }
                        className="dark-grey btn-open-log"
                        aria-label={`Ver bitácora ${log.logUID}`}
                      />

                      <button
                        type="button"
                        onClick={() =>
                          handleCopyLog(log.studentUID, log.logUID)
                        }
                        aria-label={`Copiar bitácora ${log.logUID}`}
                        disabled={copyingLogId === log.logUID}
                        className="btn-copy-log"
                      >
                        <BsCopy />
                      </button>

                      <button
                        className="btn-copy-log"
                        onClick={() => handleDelete(log.studentUID, log.logUID)}
                        aria-label={`Eliminar bitácora ${log.logUID}`}
                        disabled={deletingLogId === log.logUID}
                      >
                        <PiTrashLight />
                      </button>
                    </div>
                  </LogsRow>
                )
              })}
          </div>
        </TableContainer>
      )}
    </>
  )
}

export default LogsTable
